import { create } from 'zustand';
import { ColumnState } from 'ag-grid-community';
import { PRD2040_R01BomDto, PRD2040_R01MountDto } from './Dto_PRD2040_R01';

export interface PRD2040_R01Filter {
    productWorkNo: string;        // 공정작업번호
    productWorkItemCd: string;    // 지시품목 코드
    productWorkItemNm: string;    // 지시품목명
    compareItemCd: string;        // 비교품목 코드
    compareItemNm: string;        // 비교품목명
    outputType: 'BOM' | 'COORD';
}

export type PRD2040_R01TabType = 'BOM' | 'MOUNT';

export interface PRD2040_R01TabData {
    BOM: PRD2040_R01BomDto[];
    MOUNT: PRD2040_R01MountDto[];
}

export interface PRD2040_R01TabSelectedId {
    BOM: string | undefined;
    MOUNT: string | undefined;
}

export interface PRD2040_R01TabColumnState {
    BOM: ColumnState[] | null;
    MOUNT: ColumnState[] | null;
}

export interface PRD2040_R01TabScrollTop {
    BOM: number | null;
    MOUNT: number | null;
}

export interface Store_PRD2040_R01 {
    // 검색 조건
    PRD2040_R01Filter: PRD2040_R01Filter;
    setPRD2040_R01Filter: (filter: Partial<PRD2040_R01Filter>) => void;

    // 활성 탭
    activeTab: PRD2040_R01TabType;
    setActiveTab: (tab: PRD2040_R01TabType) => void;

    // 탭별 목록
    tabData: PRD2040_R01TabData;
    setTabData: <T extends PRD2040_R01TabType>(tab: T, list: PRD2040_R01TabData[T]) => void;

    // 탭별 선택된 행
    tabSelectedId: PRD2040_R01TabSelectedId;
    setTabSelectedId: (tab: PRD2040_R01TabType, id: string | undefined) => void;
    initTabSelectedId: (tab: PRD2040_R01TabType) => void;

    // 탭별 컬럼 상태
    tabColumnState: PRD2040_R01TabColumnState;
    setTabColumnState: (tab: PRD2040_R01TabType, state: ColumnState[] | null) => void;

    // 탭별 스크롤 위치
    tabScrollTop: PRD2040_R01TabScrollTop;
    setTabScrollTop: (tab: PRD2040_R01TabType, top: number | null) => void;

    // 초기화
    initData: () => void;
}

export const defaultPRD2040_R01Filter: PRD2040_R01Filter = {
    productWorkNo: '',
    productWorkItemCd: '',
    productWorkItemNm: '',
    compareItemCd: '',
    compareItemNm: '',
    outputType: 'BOM'
};

const defaultTabData: PRD2040_R01TabData = { BOM: [], MOUNT: [] };
const defaultTabSelectedId: PRD2040_R01TabSelectedId = { BOM: undefined, MOUNT: undefined };
const defaultTabColumnState: PRD2040_R01TabColumnState = { BOM: null, MOUNT: null };
const defaultTabScrollTop: PRD2040_R01TabScrollTop = { BOM: null, MOUNT: null };

export const usePRD2040_R01Store = create<Store_PRD2040_R01>((set) => ({
    PRD2040_R01Filter: defaultPRD2040_R01Filter,
    setPRD2040_R01Filter: (filter) => set((state) => ({
        PRD2040_R01Filter: { ...state.PRD2040_R01Filter, ...filter }
    })),

    activeTab: 'BOM',
    setActiveTab: (tab) => set({ activeTab: tab }),

    tabData: defaultTabData,
    setTabData: (tab, list) => set((state) => ({
        tabData: {
            ...state.tabData,
            [tab]: (list as any[]).map((item, idx) => ({
                ...item,
                valueIndex: item.valueIndex ?? idx + 1,
                orderIndex: item.orderIndex ?? idx + 1,
            })),
        },
    })),

    tabSelectedId: defaultTabSelectedId,
    setTabSelectedId: (tab, id) => set((state) => ({
        tabSelectedId: { ...state.tabSelectedId, [tab]: id },
    })),
    initTabSelectedId: (tab) => set((state) => ({
        tabSelectedId: { ...state.tabSelectedId, [tab]: undefined },
    })),

    tabColumnState: defaultTabColumnState,
    setTabColumnState: (tab, columnState) => set((state) => ({
        tabColumnState: { ...state.tabColumnState, [tab]: columnState },
    })),

    tabScrollTop: defaultTabScrollTop,
    setTabScrollTop: (tab, top) => set((state) => ({
        tabScrollTop: { ...state.tabScrollTop, [tab]: top },
    })),

    initData: () => set({
        PRD2040_R01Filter: defaultPRD2040_R01Filter,
        activeTab: 'BOM',
        tabData: defaultTabData,
        tabSelectedId: defaultTabSelectedId,
        tabColumnState: defaultTabColumnState,
        tabScrollTop: defaultTabScrollTop,
    }),
}));

